
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useProductSubmission = (onSubmit: (product: any) => void) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (
    name: string,
    quantity: string,
    unit: string,
    customUnit: string,
    price: string,
    minQuantity: string,
    isPoultry: boolean,
    poultryData?: {
      Age?: string;
      Santé?: string;
      Poids?: string;
      Régime?: string;
      Race?: string;
    },
    purchase_price?: string
  ) => {
    setIsLoading(true);
    console.log("Starting product submission with values:", {
      name,
      quantity,
      unit,
      price,
      purchase_price,
      minQuantity,
      isPoultry,
      poultryData
    });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error("No user found");
        toast.error("Vous devez être connecté pour ajouter un produit");
        setIsLoading(false);
        return false;
      }

      const { data: businessStocks } = await supabase
        .from('business_stocks')
        .select('id')
        .eq('owner_id', user.id)
        .limit(1)
        .maybeSingle();

      if (!businessStocks?.id) {
        console.error("No business stock found");
        toast.error("Erreur: Stock non trouvé");
        setIsLoading(false);
        return false;
      }

      const finalUnit = unit === "custom" ? customUnit : unit;
      const productNameToCheck = isPoultry ? poultryData?.Race : name;
      
      // Vérification directe dans le tableau stock_items
      const { data: existingProducts, error: queryError } = await supabase
        .from('stock_items')
        .select('id, data')
        .eq('stock_id', businessStocks.id);

      if (queryError) {
        console.error("Erreur lors de la vérification des produits existants:", queryError);
        toast.error("Erreur lors de la vérification des produits existants");
        setIsLoading(false);
        return false;
      }

      // Afficher tous les produits pour voir ce qui existe déjà
      console.log("Produits existants:", existingProducts);
      
      // Vérification manuelle avec comparaison insensible à la casse
      const hasDuplicate = existingProducts?.some(item => {
        const itemData = item.data as any;
        let itemName = "";
        
        // Récupérer le nom du produit selon qu'il s'agit d'une volaille ou non
        if (itemData.Race) {
          itemName = itemData.Race;
        } else if (itemData.name) {
          itemName = itemData.name;
        } else {
          return false; // Si pas de nom, ce n'est pas un doublon
        }
        
        const itemUnit = itemData.unit || "";
        
        console.log(`Comparing: "${itemName?.toLowerCase()}" with "${productNameToCheck?.toLowerCase()}" and "${itemUnit?.toLowerCase()}" with "${finalUnit?.toLowerCase()}"`);
        
        const nameMatches = itemName?.toLowerCase() === productNameToCheck?.toLowerCase();
        const unitMatches = itemUnit?.toLowerCase() === finalUnit?.toLowerCase();
        
        return nameMatches && unitMatches;
      });

      console.log("Doublon détecté:", hasDuplicate);

      if (hasDuplicate) {
        console.error("Un produit avec ce nom et cette unité existe déjà");
        toast.error(`Un produit avec le nom "${productNameToCheck}" et l'unité "${finalUnit}" existe déjà dans votre stock`);
        setIsLoading(false);
        return false;
      }
      
      // Préparer les données du produit
      const productData = isPoultry ? {
        Race: name,
        Age: poultryData?.Age,
        Santé: poultryData?.Santé,
        Poids: poultryData?.Poids,
        Régime: poultryData?.Régime,
        unit: finalUnit,
        price: parseFloat(price),
        minQuantity: parseInt(minQuantity),
        status: parseInt(quantity) > 0 ? "En stock" : "Rupture"
      } : {
        name,
        unit: finalUnit,
        price: parseFloat(price),
        minQuantity: parseInt(minQuantity),
        status: parseInt(quantity) > 0 ? "En stock" : "Rupture"
      };

      console.log("Inserting product with data:", productData);

      const parsedPurchasePrice = purchase_price ? parseFloat(purchase_price) : 0;
      console.log("Purchase price being saved:", parsedPurchasePrice);

      try {
        // Utiliser upsert au lieu d'insert pour éviter les conflits
        const { data: newProduct, error: insertError } = await supabase
          .from('stock_items')
          .insert({
            stock_id: businessStocks.id,
            data: productData,
            quantity: parseInt(quantity),
            purchase_price: parsedPurchasePrice
          })
          .select()
          .single();

        if (insertError) {
          console.error("Erreur lors de l'ajout du produit:", insertError);
          
          if (insertError.code === "23505") {
            toast.error(`Un produit avec le même nom et la même unité existe déjà. Essayez avec un nom ou une unité différente.`);
          } else {
            toast.error(`Erreur lors de l'ajout du produit: ${insertError.message}`);
          }
          setIsLoading(false);
          return false;
        }

        console.log("Product added successfully:", newProduct);
        toast.success("Produit ajouté avec succès");
        onSubmit(newProduct);
        return true;
      } catch (insertError: any) {
        console.error("Erreur lors de l'insertion:", insertError);
        toast.error("Erreur lors de l'ajout du produit");
        setIsLoading(false);
        return false;
      }
    } catch (error: any) {
      console.error("Erreur inattendue:", error);
      
      if (error.body && error.body.includes("23505")) {
        toast.error(`Un produit avec le même nom et la même unité existe déjà.`);
      } else {
        toast.error("Une erreur inattendue s'est produite");
      }
      setIsLoading(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleSubmit
  };
};
